import type { ReactiveSet } from '@solid-primitives/set'
import { Title } from '@solidjs/meta'
import { type Component, createSignal, Show } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { usePermissions } from '~/hooks/usePermissions'
import { useServerTable } from '~/hooks/useServerTable'
import { type Path, useNavigate } from '~/router'
import { type FormType, formService, publicFormUrl } from '~/services/form.service'
import { resolveError } from '~/utils/error'

import { P } from '~/utils/permissions'

const statusColors: Record<string, 'default' | 'success' | 'warning'> = {
  draft: 'default',
  published: 'success',
  closed: 'warning'
}

const statusLabels: Record<string, string> = {
  draft: 'Borrador',
  published: 'Publicado',
  closed: 'Cerrado'
}

const FormulariosPage: Component = () => {
  const toast = useToast()
  const alert = useAlert()
  const navigate = useNavigate()
  const { can } = usePermissions()

  // Búsqueda server-side
  const [_search, debouncedSearch, setSearch] = useDebouncedSignal('')

  const table = useServerTable<FormType>({
    fetcher: params => formService.list(params as Parameters<typeof formService.list>[0]),
    params: () => {
      const q = debouncedSearch().trim()
      return q ? { title: `%${q}%` } : {}
    }
  })

  /** Sólo se puede borrar lo que nadie ha respondido; el resto se cierra. */
  const handleDelete = async (form: FormType) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar formulario',
      message: `Se eliminará «${form.title}». El enlace dejará de funcionar.`
    })
    if (!ok) return

    try {
      await formService.delete(form.id)
      toast.addToast({ title: 'Formulario eliminado', type: 'success' })
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  // ── Selección masiva ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<number>>(new Set())

  const handleSelectionChange = (selected: ReactiveSet<unknown>) => {
    const next = new Set<number>()
    for (const value of selected.values()) next.add(value as number)
    setSelectedIds(next)
  }

  const handleBulkDelete = async () => {
    const ids = [...selectedIds()]
    if (ids.length === 0) return
    const ok = await alert.confirmDelete({
      title: 'Eliminar formularios',
      message: `¿Eliminar ${ids.length} ${ids.length === 1 ? 'formulario' : 'formularios'}? Los que ya tengan respuestas se omiten: se cierran, no se eliminan.`
    })
    if (!ok) return
    try {
      const res = await formService.bulkDelete(ids)
      const skippedNote = res.skipped > 0 ? ` (${res.skipped} omitidos por tener respuestas)` : ''
      toast.addToast({ title: `${res.deleted} ${res.deleted === 1 ? 'formulario eliminado' : 'formularios eliminados'}${skippedNote}`, type: 'success' })
      setSelectedIds(new Set<number>())
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  /** Cerrar deja el formulario visible pero sin admitir más respuestas. */
  const toggleOpen = async (form: FormType) => {
    const closing = form.status !== 'closed'

    if (closing) {
      const ok = await alert.confirm({
        title: 'Cerrar formulario',
        message: `«${form.title}» seguirá visible con el enlace, pero dejará de recibir respuestas.`,
        confirmText: 'Cerrar formulario'
      })
      if (!ok) return
    }

    try {
      await formService.update(form.id, { title: form.title, status: closing ? 'closed' : 'published' })
      toast.addToast({ title: closing ? 'Formulario cerrado' : 'Formulario reabierto', type: 'success' })
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const copyLink = async (form: FormType) => {
    if (form.status === 'draft') {
      toast.addToast({ title: 'Publícalo primero para poder compartir el enlace', type: 'warning' })
      return
    }

    try {
      await navigator.clipboard.writeText(publicFormUrl(form.slug))
      toast.addToast({ title: 'Enlace copiado', type: 'success' })
    } catch {
      // Sin permiso de portapapeles (o http sin localhost): al menos se lo mostramos.
      alert.info('Enlace del formulario', publicFormUrl(form.slug))
    }
  }

  const columns = [
    {
      header: 'Formulario',
      sticky: 'left' as const,
      minWidth: 260,
      accessor: (f: FormType) => (
        <div>
          <div class="font-medium text-foreground">{f.title}</div>
          <div class="text-xs text-foreground-subtle">/f/{f.slug}</div>
        </div>
      )
    },
    {
      header: 'Preguntas',
      accessor: (f: FormType) => String(f.fields?.length ?? 0)
    },
    {
      header: 'Respuestas',
      accessor: (f: FormType) => <span class={f.responsesCount ? 'font-semibold text-foreground' : 'text-foreground-subtle'}>{f.responsesCount ?? 0}</span>
    },
    {
      header: 'Estado',
      accessor: (f: FormType) => <Badge color={statusColors[f.status]}>{statusLabels[f.status]}</Badge>
    }
  ]

  return (
    <>
      <Title>Formularios | Florez Jaramillo</Title>

      <Show when={table.error()}>
        <div class="rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3 text-sm text-danger mb-4">{resolveError(table.error())}</div>
      </Show>

      <Table<FormType>
        title="Formularios"
        subtitle="Diseña un formulario, publícalo y comparte su enlace"
        headerActions={
          <Show when={can(P.forms.create)}>
            <Button onClick={() => navigate('/admin/formularios/nuevo' as Path)} prependIcon="material-symbols-light:add-circle-outline">
              Nuevo Formulario
            </Button>
          </Show>
        }
        data={table.data()}
        columns={columns}
        keyField="id"
        loading={table.loading()}
        searchable
        searchPlaceholder="Buscar formulario..."
        onSearch={setSearch}
        serverSide
        selectable={can(P.forms.delete)}
        selectableColor="danger"
        onSelectionChange={handleSelectionChange}
        onDeleteSelected={() => void handleBulkDelete()}
        total={table.total()}
        page={table.page()}
        perPage={table.perPage()}
        onPageChange={table.onPageChange}
        onPerPageChange={table.onPerPageChange}
        headSticky
        emptyMessage="Aún no has creado formularios"
        actions={form => (
          <ButtonGroup size="sm" variant="soft">
            <Button color="info" onClick={() => navigate(`/admin/formularios/respuestas/${form.id}` as Path)} tooltip="Ver respuestas" icon="material-symbols-light:table-outline" />
            <Button color="success" onClick={() => copyLink(form)} tooltip="Copiar enlace público" icon="material-symbols-light:link" />
            <Show when={can(P.forms.update)}>
              <Button color="warning" onClick={() => navigate(`/admin/formularios/${form.id}` as Path)} tooltip="Editar" icon="material-symbols-light:edit-outline" />
            </Show>
            <Show
              when={(form.responsesCount ?? 0) === 0}
              fallback={
                <Show when={can(P.forms.update)}>
                  <Button
                    color={form.status === 'closed' ? 'primary' : 'danger'}
                    onClick={() => toggleOpen(form)}
                    tooltip={form.status === 'closed' ? 'Reabrir' : 'Cerrar: ya tiene respuestas y no se puede eliminar'}
                    icon={form.status === 'closed' ? 'material-symbols-light:lock-open-outline' : 'material-symbols-light:lock-outline'}
                  />
                </Show>
              }
            >
              <Show when={can(P.forms.delete)}>
                <Button color="danger" onClick={() => handleDelete(form)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
              </Show>
            </Show>
          </ButtonGroup>
        )}
      />

      <alert.AlertComponent />
    </>
  )
}

export default FormulariosPage
